import { captureException } from '@sentry/nextjs'
import useSWR from 'swr'

export async function sharepointEmbedUrlFetcher(url: string) {
  const response = await fetch(url)

  if (!response.ok) {
    const text = await response.text()
    let errorMessage = response.statusText || 'request failed'
    if (text) {
      try {
        const parsed = JSON.parse(text)
        if (typeof parsed?.error === 'string') errorMessage = parsed.error
      } catch {
        // non-JSON body — keep statusText
      }
    }

    throw new Error(errorMessage, {
      cause: {
        status: response.status,
      },
    })
  }

  return response.json() as Promise<{ embedUrl: string }>
}

export function getErrorStatus(error: unknown) {
  if (!(error instanceof Error)) return undefined
  const { status } = (error.cause ?? {}) as { status?: unknown }

  return typeof status === 'number' ? status : undefined
}

// A broken or revoked sharing link is a permanent, user-caused outcome:
// retrying cannot fix it and it is not worth a Sentry event.
export function isClientError(error: unknown) {
  const status = getErrorStatus(error)

  return status !== undefined && status >= 400 && status < 500
}

// Only an actual server response is worth reporting. Errors without a status
// are aborted or offline fetches, which the global SWR handler skips as well.
export function isServerError(error: unknown) {
  const status = getErrorStatus(error)

  return status !== undefined && status >= 500
}

export function useSharepointEmbedUrl(sharedUrl: string) {
  const { data, error, isLoading } = useSWR(
    `/api/sharepoint-embed-url?sharedUrl=${encodeURIComponent(sharedUrl)}`,
    sharepointEmbedUrlFetcher,
    {
      // the embed url is stable per shared url
      revalidateOnFocus: false,
      shouldRetryOnError: (err) => !isClientError(err),
      // overrides the global SWR onError, which would report every failed
      // attempt to Sentry and show a "reload the page" toast
      onError: (err) => {
        if (isServerError(err)) captureException(err)
      },
    },
  )

  return {
    sharepointEmbedUrl: data?.embedUrl,
    error,
    notFound: getErrorStatus(error) === 404,
    isLoading,
  }
}
